import PlaceModel               from '@models/map/PlaceModel';
import PlaceTypeModel           from '@models/map/PlaceTypeModel';
import currentEditorStore       from '@/TokamapEditor/stores/currentEditorStore';
import { fetchEntityFromPlace } from '@/TokamapEditor/stores/EditorMercureListener';
import themeStore               from '@/TokamapEditor/stores/themeStore';
import { Shape }                from '@tokamap/core';
import { PLACE_LABEL_SOURCE }   from '@/TokamapEditor/helpers/placeLinkedContent';
import { PLACE_IMAGE_SOURCE }   from '@/TokamapEditor/helpers/placeLinkedContent';
import asSingleHistoryEntry     from '@/TokamapEditor/helpers/asSingleHistoryEntry';
import { badgeEligibleShapes }  from '@/TokamapEditor/helpers/shapeBadge';

export default async function linkPlaceToShape(shapeIds: string | string[], place: PlaceModel) {
	const entity = await fetchEntityFromPlace(place, false);

	await asSingleHistoryEntry(() => applyPlaceLink(shapeIds, place, entity));
}

async function applyPlaceLink(shapeIds: string | string[], place: PlaceModel, entity: any) {
	const editor = currentEditorStore.get;
	const imageSrc = entity?.entity.image || '';

	const ids = Array.isArray(shapeIds) ? shapeIds : [shapeIds];
	const shapes = ids
		.map(id => editor.getAllShapes().find(s => s.id === id))
		.filter((s): s is NonNullable<typeof s> => s !== undefined);

	// Une seule entrée d'historique/sauvegarde pour tout le groupe (voir CLAUDE.md "Sélection
	// multiple") : seules les formes qui n'ont pas déjà `action: 'open_place'` sont incluses, pour ne
	// pas ajouter d'entrée sans effet pour celles qui l'ont déjà.
	const idsNeedingAction = shapes.filter(s => s.action !== 'open_place').map(s => s.id);

	if (idsNeedingAction.length) {
		editor.setShapeAction(idsNeedingAction, 'open_place');
	}

	editor.setShapeEntity(ids, entity);

	const contentShapes = shapes.filter(s => s.shapeKind !== 'line');
	const contentIds = contentShapes.map(s => s.id);

	if (contentIds.length) {
		editor.setShapeText(contentIds, { text: place.label || '', source: PLACE_LABEL_SOURCE });
		await editor.setShapeImage(contentIds, { src: imageSrc, source: PLACE_IMAGE_SOURCE });
	}

	// Un lieu s'affiche par défaut en marqueur au-dessus de sa forme ; une forme déjà en marqueur garde ses réglages.
	const badgeIds = badgeEligibleShapes(contentShapes).filter(s => !s.badge).map(s => s.id);

	if (badgeIds.length) editor.setShapeBadge(badgeIds, {});

	const placeType = await fetchPlaceType(place);

	if (!placeType) return;

	// La bordure reprend la couleur du type : la forme se rattache visuellement à sa catégorie dès la
	// liaison. Les formes-images n'ont pas de bordure affichée.
	const borderIds = shapes.filter(s => s.shapeKind !== 'image').map(s => s.id);

	if (placeType.color && borderIds.length) {
		editor.setShapeStyle(borderIds, { borderColor: placeType.color });
	}

	await applyPlaceTypeTheme(shapes, placeType);
}

async function fetchPlaceType(place: PlaceModel) {
	const placeTypeId = place.getId('placeType');

	if (!placeTypeId) return undefined;

	const placeType = new PlaceTypeModel({ id: placeTypeId });

	await placeType.fetch();

	return placeType;
}

// Le thème du type s'applique de lui-même à la liaison, sans question. Seulement aux formes qui n'ont
// pas encore de thème : un thème choisi à la main sur la forme n'est pas écrasé en silence.
async function applyPlaceTypeTheme(shapes: Shape[], placeType: PlaceTypeModel) {
	const themeId = placeType.getResolvableId('theme');

	if (themeId === undefined) return;

	if (!themeStore.isLoaded) await themeStore.load();

	const theme = themeStore.getTheme(String(themeId));

	if (!theme) return;

	const eligibleIds = shapes
		.filter(s => s.shapeKind !== 'image' && !s.themeId)
		.map(s => s.id);

	if (eligibleIds.length) currentEditorStore.get.setShapeThemeId(eligibleIds, theme.id);
}
